/**
 * CSS Validator Utility
 *
 * Validates CSS syntax using both internal validation and CSSLint.
 *
 * @since 1.0.0
 */

import type { CssValidationError, CssValidationResult } from './types';

/**
 * Default CSSLint rules configuration.
 *
 * @since 1.0.0
 */
const DEFAULT_CSSLINT_RULES: Record<string, boolean> = {
  'adjoining-classes': false,
  'box-model': true,
  'box-sizing': false,
  'bulletproof-font-face': true,
  'compatible-vendor-prefixes': true,
  'display-property-grouping': true,
  'duplicate-background-images': true,
  'duplicate-properties': true,
  'empty-rules': true,
  errors: true,
  'fallback-colors': true,
  'font-faces': true,
  'font-sizes': false,
  gradients: true,
  ids: false,
  import: true,
  important: false,
  'known-properties': true,
  'outline-none': false,
  'overqualified-elements': false,
  'qualified-headings': false,
  'regex-selectors': false,
  'rules-count': false,
  'selector-max-approaching': false,
  'selector-max': false,
  shorthand: true,
  'star-property-hack': true,
  'text-indent': true,
  'underscore-property-hack': true,
  'unique-headings': false,
  'universal-selector': false,
  'unqualified-attributes': false,
  'vendor-prefix': true,
  'zero-units': true,
};

/**
 * Validates CSS syntax using CSSLint.
 *
 * @since 1.0.0
 *
 * @param css   The CSS string to validate.
 * @param rules Optional CSSLint rules configuration.
 *
 * @return Array of validation errors from CSSLint.
 */
const validateWithCSSLint = (
  css: string,
  rules?: Record<string, boolean>
): CssValidationError[] => {
  if (!window.CSSLint?.verify) {
    return [];
  }

  try {
    const result = window.CSSLint.verify(css, rules ?? DEFAULT_CSSLINT_RULES);
    return result.messages
      .filter((message) => message.type === 'error' || message.type === 'warning')
      .map((message) => ({
        line: message.line,
        col: message.col,
        message: message.message,
        type: message.type,
        source: 'csslint' as const,
        rule: {
          id: message.rule.id,
          name: message.rule.name,
          desc: message.rule.desc,
        },
      }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('CSSLint validation failed:', error);
    return [];
  }
};

/**
 * Validates CSS syntax using internal browser-based validation.
 *
 * @since 1.0.0
 *
 * @param css The CSS string to validate.
 *
 * @return Array of validation errors from internal validation.
 */
const validateWithInternalParser = (css: string): CssValidationError[] => {
  if (!css?.trim()) {
    return [];
  }

  const errors: CssValidationError[] = [];

  try {
    // Create a temporary style element to test CSS parsing
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);

    // Check if the CSS was parsed successfully
    const sheet = style.sheet as CSSStyleSheet;

    if (sheet?.cssRules) {
      // CSS parsed successfully, but we still need to check for syntax errors
      // Split CSS into lines for better error reporting
      const lines = css.split('\n');

      // Basic syntax validation
      let braceCount = 0;
      let inComment = false;
      let inString = false;
      let stringChar = '';

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex] ?? '';

        for (let charIndex = 0; charIndex < line.length; charIndex++) {
          const char = line[charIndex] ?? '';
          const nextChar = line[charIndex + 1] ?? '';

          // Handle comments
          if (!inString && char === '/' && nextChar === '*') {
            inComment = true;
            charIndex++; // Skip next character
            continue;
          }

          if (inComment && char === '*' && nextChar === '/') {
            inComment = false;
            charIndex++; // Skip next character
            continue;
          }

          if (inComment) {
            continue;
          }

          // Handle strings
          if (!inString && (char === '"' || char === "'")) {
            inString = true;
            stringChar = char;
            continue;
          }

          if (inString && char === stringChar) {
            inString = false;
            stringChar = '';
            continue;
          }

          if (inString) {
            continue;
          }

          // Count braces
          if (char === '{') {
            braceCount++;
          } else if (char === '}') {
            braceCount--;
            if (braceCount < 0) {
              errors.push({
                line: lineIndex + 1,
                message: 'Unexpected closing brace',
                type: 'error',
                source: 'internal',
              });
            }
          }
        }
      }

      // Check for unmatched braces
      if (braceCount > 0) {
        errors.push({
          line: lines.length,
          message: `${braceCount} unclosed brace${braceCount > 1 ? 's' : ''}`,
          type: 'error',
          source: 'internal',
        });
      }
    }

    // Clean up
    document.head.removeChild(style);
  } catch (error) {
    // If CSS parsing fails completely, it's definitely invalid
    errors.push({
      line: 1,
      message: error instanceof Error ? error.message : 'Invalid CSS syntax',
      type: 'error',
      source: 'internal',
    });
  }

  return errors;
};

/**
 * Validates CSS syntax using both internal validation and CSSLint.
 *
 * @since 1.0.0
 *
 * @param css   The CSS string to validate.
 * @param rules Optional CSSLint rules configuration.
 *
 * @return Validation result with error information from both validators.
 */
export const validateCss = (css: string, rules?: Record<string, boolean>): CssValidationResult => {
  if (!css?.trim()) {
    return {
      isValid: true,
      errors: [],
    };
  }

  // Get errors from both validators
  const internalErrors = validateWithInternalParser(css);
  const cssLintErrors = validateWithCSSLint(css, rules);

  // Combine all errors
  const allErrors = [...internalErrors, ...cssLintErrors];

  // Remove duplicate errors (same line and similar message)
  const uniqueErrors = allErrors.filter((error, index, array) => {
    return !array
      .slice(0, index)
      .some(
        (prevError) =>
          prevError.line === error.line &&
          prevError.message.toLowerCase().includes(error.message.toLowerCase().substring(0, 20))
      );
  });

  // Sort errors by line number
  uniqueErrors.sort((a, b) => a.line - b.line);

  return {
    isValid: uniqueErrors.filter((error) => error.type === 'error').length === 0,
    errors: uniqueErrors,
  };
};
